import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { queryTestRuns } from '@/features/executions/data';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import {
  buildTestDetailPath,
  buildTestRunPath,
} from '@/features/shared/components/tableSystem/tableRoutes';
import { TableV2DurationCell } from '@/features/shared/components/tableSystem/TableV2DurationCell';
import {
  queryAllTableV2NestedRows,
  tableV2NestedContentSx,
  tableV2NestedDepthSx,
  type TableV2NestedTableDepth,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2DateTimeCell,
  TableV2HeaderLabel,
  TableV2NameCell,
  TableV2NestedCollapse,
  TableV2SortableHeader,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2NestedBodyRowHeightPx,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { formatTableV2RunNumber } from '@/features/shared/components/tableSystem/TableV2RunFormatting';
import { TableV2SkeletonRow } from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { resolveTableV2NestedSkeletonRowCount } from '@/features/shared/components/tableSystem/TableV2SkeletonRows';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { buildEqFilter, buildEqFilterValue, mergeFilters } from '@/ui/composites/grid/gridFilters';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Text } from '@/ui/primitives';
import type { useTestRunsTableRowActions } from '@/features/executions/components/useTestRunsTableRowActions';
import type { TableV2SkeletonColumnKind } from '@/features/shared/components/tableSystem/TableV2Primitives';
import type { ApiSort } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, TestRunDto } from '@flowqa/shared';

type SortableFeatureTestRunField = 'durationMs' | 'runNumber' | 'status' | 'timestamp';

export type FeatureTestRunActions = Pick<
  ReturnType<typeof useTestRunsTableRowActions>,
  'deleteRowPendingId' | 'onDeleteRow' | 'onUpdateRowTagsColor' | 'updateRowTagsColorPendingId'
> & {
  onEditRow: (row: TestRunDto) => void;
};

type FeatureTestRunsNestedTableProps = {
  childCount: number;
  depth?: TableV2NestedTableDepth;
  expanded: boolean;
  featureId: string;
  projectId: string;
  testRunActions: FeatureTestRunActions;
};

const defaultFeatureTestRunSort = { field: 'timestamp', direction: 'desc' } satisfies ApiSort;

const featureTestRunSkeletonColumns = [
  'name',
  'status',
  'duration',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

const featureTestRunColgroup = (
  <colgroup>
    <col width="30%" />
    <col width="13%" />
    <col width="13%" />
    <col width="20%" />
    <col width="20%" />
    <col width={tableV2ActionsColumnWidth} />
  </colgroup>
);

const buildFeatureTestRunHeader = ({
  sort,
  onSortChange,
}: {
  sort: ApiSort;
  onSortChange: (field: SortableFeatureTestRunField) => void;
}): ReactElement => (
  <TableRow>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="runNumber"
        label="Test Run"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="status"
        label="Status"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="durationMs"
        label="Duration"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="timestamp"
        label="Ran At"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2HeaderLabel label="Tags" />
    </TableCell>
    <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
      <TableV2ActionsHeaderIcon />
    </TableCell>
  </TableRow>
);

const FeatureTestRunNestedRow = ({
  actions,
  depth,
  row,
}: {
  actions: FeatureTestRunActions;
  depth: TableV2NestedTableDepth;
  row: TestRunDto;
}): ReactElement => {
  const name = row.displayName ?? row.testName ?? row.name ?? row.id;
  const detailPath = buildTestDetailPath(row.testSlug ?? row.testId);
  const runPath = buildTestRunPath(row.testSlug ?? row.testId, row.runNumber);
  const runNumberLabel = formatTableV2RunNumber(row.runNumber);

  return (
    <TableRow
      sx={[
        tableV2NestedDepthSx[depth].bodyRow,
        {
          '& > td': {
            borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
            height: `${String(tableV2NestedBodyRowHeightPx)}px`,
            py: s('sm'),
            verticalAlign: 'middle',
          },
        },
      ]}
    >
      <TableCell>
        <TableV2NameCell
          href={detailPath}
          primary={name}
          secondary={runNumberLabel}
          secondaryHref={runPath}
        />
      </TableCell>
      <TableCell>
        <TableV2StatusChip status={row.status} />
      </TableCell>
      <TableCell>
        <TableV2DurationCell value={row.durationMs} />
      </TableCell>
      <TableCell>
        <TableV2DateTimeCell value={row.timestamp} />
      </TableCell>
      <TableCell>
        <TableV2TagGroup tags={row.tags ?? []} />
      </TableCell>
      <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
        <TableActionsCell
          rowName={name}
          verticalAlign="center"
          deleteActionLabel={getEntityActionLabel('delete', 'Test Run')}
          editActionLabel={getEntityActionLabel('edit', 'Test Run')}
          editTagsEntityNoun="test run"
          rowTags={row.tags ?? []}
          onEdit={() => {
            actions.onEditRow(row);
          }}
          onUpdateTagsColor={(input) => {
            actions.onUpdateRowTagsColor({ row, ...input });
          }}
          updateTagsColorLoading={actions.updateRowTagsColorPendingId === row.id}
          onDelete={() => {
            actions.onDeleteRow(row);
          }}
          disableDelete={actions.deleteRowPendingId === row.id}
        />
      </TableCell>
    </TableRow>
  );
};

export const FeatureTestRunsNestedTable = ({
  childCount,
  depth = 1,
  expanded,
  featureId,
  projectId,
  testRunActions,
}: FeatureTestRunsNestedTableProps): ReactElement => {
  const [sort, setSort] = useState<ApiSort>(defaultFeatureTestRunSort);
  const handleSortChange = (field: SortableFeatureTestRunField): void => {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };
  const header = buildFeatureTestRunHeader({ sort, onSortChange: handleSortChange });
  const query = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.testRuns,
      'v2-feature-nested',
      projectId,
      featureId,
      sort.field,
      sort.direction,
    ),
    enabled: expanded,
    queryFn: async (): Promise<QueryResult<TestRunDto>> =>
      await queryAllTableV2NestedRows(
        {
          page: 1,
          pageSize: 1,
          sorts: [sort],
          filters: mergeFilters(
            buildEqFilter('featureId', featureId),
            buildEqFilterValue('isLatest', true),
          ),
        },
        async (input) => await queryTestRuns(projectId, undefined, input),
      ),
    staleTime: 30_000,
  });
  const skeletonRowCount = resolveTableV2NestedSkeletonRowCount(childCount);

  return (
    <TableV2NestedCollapse expanded={expanded}>
      <Box sx={tableV2NestedContentSx}>
        {query.isLoading ? (
          <Box sx={tableV2NestedDepthSx[depth].frame}>
            <Table aria-label="Feature test runs loading" sx={tableV2NestedDepthSx[depth].table}>
              {featureTestRunColgroup}
              <TableHead>{header}</TableHead>
              <TableBody>
                {Array.from({ length: skeletonRowCount }, (_item, index) => (
                  <TableV2SkeletonRow
                    key={`feature-test-runs-nested-skeleton-${String(index)}`}
                    columns={featureTestRunSkeletonColumns}
                    isLastVisibleRow={index === skeletonRowCount - 1}
                    rowIndex={index}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : query.data && query.data.items.length > 0 ? (
          <Box sx={tableV2NestedDepthSx[depth].frame}>
            <Table aria-label="Feature test runs" sx={tableV2NestedDepthSx[depth].table}>
              {featureTestRunColgroup}
              <TableHead>{header}</TableHead>
              <TableBody>
                {query.data.items.map((testRun) => (
                  <FeatureTestRunNestedRow
                    key={testRun.id}
                    actions={testRunActions}
                    depth={depth}
                    row={testRun}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Text role="body" color="textSecondary">
            No data
          </Text>
        )}
      </Box>
    </TableV2NestedCollapse>
  );
};
